/**
 * ApprovalCard - Inline approve/deny UI for tools with requires_approval
 *
 * Rendered inside ToolCallPartRendererUser when a task is paused
 * waiting for user approval of a tool call.
 * Modifies the Task's deferred_responses field via useEditable.
 */

import React, { useState } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import { Button } from '@sourcepilot/ui/framework/ui/atoms/button';
import { useEditable } from '@sourcepilot/ui/framework/data/hooks/use-editable';
import { EntityLink } from './tool-result-card';

interface ApprovalCardProps {
  task: any;
  toolCallId: string;
  displayName: string;
  args?: Record<string, unknown>;
  /** Optional preview written by the backend before the agent paused.
   *  Currently emitted by auto-generated delete tools (see
   *  `_wrap_delete_with_preview_approval` in registry_tools.py): the
   *  shim runs a dry-run before raising CallDeferred and stashes the
   *  result on `ToolCallPart.pending_approval_preview`. When present,
   *  we render the cascade scope instead of the raw tool args, so the
   *  user knows exactly what the destructive operation will affect. */
  pendingApprovalPreview?: PendingApprovalPreview | null;
}

interface PendingApprovalPreview {
  kind?: string;
  model?: string;
  description?: string;
  args?: Record<string, unknown>;
  preview?: {
    deleted_ids?: string[];
    count?: number;
    message?: string;
    [key: string]: unknown;
  };
  /** Server-stamped entity refs (post egress enrichment): each item
   *  carries `{uuid, type, name, parent}` ready to feed into EntityLink. */
  deleted?: Array<{
    uuid: string;
    type?: string;
    name?: string;
    parent?: { type: string; uuid: string };
  }>;
}

/** A tool call auto-approved because it ran inside a PROPOSAL (Tom,
 *  2026-10-13: an agent making a proposal is never left waiting on a
 *  confirmation; its edits are reviewed at accept). The server marks it on
 *  `ToolCallPart.pending_approval_preview` as
 *  `{kind: "auto_approved_in_proposal", proposal: <lane>}`: a label, never an
 *  Approve/Deny card. */
export function isAutoApprovedInProposal(preview: unknown): preview is { kind: "auto_approved_in_proposal"; proposal?: string } {
  return !!preview && typeof preview === "object" && (preview as { kind?: unknown }).kind === "auto_approved_in_proposal";
}

export function AutoApprovedLabel({ proposal }: { proposal?: string }) {
  return (
    <span
      className="rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] text-indigo-800"
      title={proposal ? `Ran without asking: it was made in ${proposal}, and is reviewed when the proposal is accepted` : "Ran without asking: it is reviewed when the proposal is accepted"}
      data-testid="auto-approved-in-proposal"
    >
      auto-approved in a proposal
    </span>
  );
}

export function ApprovalCard({
  task,
  toolCallId,
  displayName,
  args,
  pendingApprovalPreview,
}: ApprovalCardProps) {
  const editable = useEditable(task);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResponse = async (approved: boolean) => {
    if (!editable) return;
    setIsSubmitting(true);
    setError(null);
    try {
      // Merge with existing responses so multiple approvals accumulate
      const existing = (task as any).deferred_responses
        ? JSON.parse((task as any).deferred_responses)
        : {};
      const responses = {
        ...existing,
        [toolCallId]: { approved, message: approved ? 'User approved' : 'User denied' },
      };
      (editable as any).deferred_responses = JSON.stringify(responses);
      await (editable as any).save();
    } catch (err: any) {
      console.error('Failed to submit approval:', err);
      setError(err.message || 'Failed to submit');
    } finally {
      // Clear the spinner regardless — the save completed (round-trip
      // to backend done). The card stays mounted until the task resumes
      // (which only happens once ALL parallel approvals have answers),
      // and we want to show a "submitted" state in the meantime, not a
      // permanent spinner.
      setIsSubmitting(false);
    }
  };

  // Has THIS specific tool_call_id already been answered? When N
  // approvals are pending in parallel, the user's click is recorded
  // immediately in `task.deferred_responses`; the actual resume only
  // fires once ALL N have been answered. We render a compact
  // "submitted" badge for cards already answered so the user has a
  // clear signal which they clicked, while the rest of the batch
  // stays approve/deny-actionable.
  const existingResponses: Record<string, { approved?: boolean }> =
    (() => {
      try {
        return (task as any)?.deferred_responses
          ? JSON.parse((task as any).deferred_responses)
          : {};
      } catch {
        return {};
      }
    })();
  const submittedResponse = existingResponses[toolCallId];
  if (submittedResponse !== undefined) {
    return (
      <div
        className={`mt-2 text-xs flex items-center gap-1.5 ${
          submittedResponse.approved ? 'text-green-700' : 'text-red-700'
        }`}
      >
        {submittedResponse.approved ? (
          <Check className="h-3 w-3" />
        ) : (
          <X className="h-3 w-3" />
        )}
        <span>{submittedResponse.approved ? 'Approved' : 'Denied'}</span>
      </div>
    );
  }

  // Show relevant args (excluding 'explanation')
  const relevantArgs = args
    ? Object.entries(args).filter(([key]) => key !== 'explanation')
    : [];

  // Delete-with-preview tools attach the dry-run cascade scope on
  // ToolCallPart.pending_approval_preview. When present, render that
  // instead of the raw args — it tells the user concretely what the
  // operation would delete.
  const isDeletePreview =
    pendingApprovalPreview?.kind === 'delete_with_approval';
  const deletePreview = isDeletePreview ? pendingApprovalPreview : null;
  const cascadeItems = deletePreview?.deleted ?? [];
  const cascadeCount =
    deletePreview?.preview?.count ?? cascadeItems.length;

  return (
    <div className="mt-2 border border-amber-200 rounded-lg p-3 bg-amber-50">
      <div className="text-sm text-gray-700 mb-2">
        <span className="font-medium">{displayName}</span>
        {isDeletePreview ? (
          <> will delete{cascadeCount > 1 ? <> <strong>{cascadeCount}</strong> nodes</> : <> this node</>} — approve to commit?</>
        ) : (
          <> needs your approval</>
        )}
      </div>
      {isDeletePreview && cascadeCount > 0 && (
        <div className="text-xs text-gray-700 mb-3 bg-white border border-amber-200 rounded px-2 py-1.5 space-y-1">
          <div className="font-medium text-gray-600">
            Will be deleted{cascadeCount > 1 ? ` (${cascadeCount})` : ''}:
          </div>
          {cascadeItems.slice(0, 8).map((item) => (
            <div key={item.uuid} className="text-[11px]">
              <EntityLink verb="Deleted" entity={item} />
            </div>
          ))}
          {cascadeItems.length > 8 && (
            <div className="text-[11px] text-gray-500 italic">
              …and {cascadeItems.length - 8} more
            </div>
          )}
        </div>
      )}
      {!isDeletePreview && relevantArgs.length > 0 && (
        <div className="text-xs text-gray-600 mb-3 space-y-1">
          {relevantArgs.slice(0, 5).map(([key, value]) => (
            <div key={key}>
              <span className="font-medium">{key}:</span>{' '}
              <span className="truncate">
                {typeof value === 'string' ? value : JSON.stringify(value)}
              </span>
            </div>
          ))}
        </div>
      )}
      {error && (
        <div className="text-xs text-red-600 mb-2">{error}</div>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="bg-green-50 border-green-300 hover:bg-green-100 text-green-700"
          onClick={() => handleResponse(true)}
          disabled={isSubmitting}
        >
          {isSubmitting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="bg-red-50 border-red-300 hover:bg-red-100 text-red-700"
          onClick={() => handleResponse(false)}
          disabled={isSubmitting}
        >
          <X className="h-3 w-3 mr-1" />
          Deny
        </Button>
      </div>
    </div>
  );
}
